import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import ConflictIssueCard from './ConflictIssueCard'
import DuplicateIssueCard from './DuplicateIssueCard'
import { groupIssues } from './groupIssues'
import LocaleSuggestions from './LocaleSuggestions'
import MissingLocaleTable from './MissingLocaleTable'
import PageLoader from '../../../components/PageLoader'
import TablePagination from '../../../components/TablePagination'
import WellDone from '../../../components/WellDone'

const IssuesList = ({
  bulkUpdateStatus = 'idle',
  fetchingStatus = 'idle',
  filteringStatus = 'idle',
  items = null,
  locales = [],
  localeSuggestions = [],
  localesFetchingStatus = 'idle',
  moveToPage,
  onBulkApply,
  onBulkApplyAll,
  onPathSearch,
  paginationCurrentPage = 1,
  paginationTotalPages = 1,
  projectSlug,
  totalCount = null
}) => {
  const { t } = useTranslation('ProjectsPage')

  const isLoading = fetchingStatus === 'fetching'
  const isFiltering = filteringStatus === 'filtering'
  const isReady = [fetchingStatus, filteringStatus].includes('succeeded')

  if (isLoading) {
    return (
      <div className="d-flex flex-column flex-fill justify-content-center align-items-center">
        <PageLoader message={t('ProjectStringsAlerts.fetching')} />
      </div>
    )
  }

  if (isReady && (totalCount === 0 || !items || items.length === 0)) {
    return <WellDone />
  }

  if (!items || items.length === 0) {
    return null
  }

  const groupedIssues = groupIssues(items)
  const { duplicates, conflicts } = groupedIssues
  const hasDuplicatesOrConflicts = duplicates.length > 0 || conflicts.length > 0
  const missingLocaleCount = items.filter((item) => item.locale === '').length

  const renderDuplicateCard = (issue, index) => (
    <DuplicateIssueCard
      key={`duplicate-${issue.key}-${issue.locale}-${index}`}
      issue={issue}
      projectSlug={projectSlug}
    />
  )

  const renderConflictCard = (issue, index) => (
    <ConflictIssueCard
      key={`conflict-${issue.key}-${issue.locale}-${index}`}
      issue={issue}
      projectSlug={projectSlug}
    />
  )

  return (
    <div className="d-flex flex-column flex-fill">
      {isFiltering && (
        <div className="d-flex flex-row justify-content-center mb-3">
          <PageLoader message={t('ProjectStringsAlerts.fetching')} />
        </div>
      )}

      {/* Locale Suggestions for bulk fixing missing locales */}
      <LocaleSuggestions
        bulkUpdateStatus={bulkUpdateStatus}
        localeSuggestions={localeSuggestions}
        onApply={onBulkApply}
        onApplyAll={onBulkApplyAll}
      />

      {/* Table view for missing locale strings */}
      {missingLocaleCount > 0 && (
        <MissingLocaleTable
          bulkUpdateStatus={bulkUpdateStatus}
          items={items}
          locales={locales}
          localesFetchingStatus={localesFetchingStatus}
          moveToPage={moveToPage}
          onBulkApply={onBulkApply}
          onPathSearch={onPathSearch}
          paginationCurrentPage={paginationCurrentPage}
          paginationTotalPages={paginationTotalPages}
          totalCount={totalCount}
        />
      )}

      {/* Cards for duplicates and conflicts */}
      {hasDuplicatesOrConflicts && (
        <div className="issues-list">
          {duplicates.map((issue, index) => renderDuplicateCard(issue, index))}
          {conflicts.map((issue, index) => renderConflictCard(issue, index))}
        </div>
      )}

      {/* Only show pagination if there are duplicates/conflicts (table has its own) */}
      {hasDuplicatesOrConflicts && (
        <div className="d-flex flex-row justify-content-center mt-3">
          <TablePagination
            currentPage={paginationCurrentPage}
            onPaginationClick={(page) => moveToPage(page)}
            totalPages={paginationTotalPages}
          />
        </div>
      )}
    </div>
  )
}

IssuesList.propTypes = {
  bulkUpdateStatus: PropTypes.string,
  fetchingStatus: PropTypes.string,
  filteringStatus: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({
    conflict: PropTypes.bool,
    duplicated: PropTypes.bool,
    id: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    locale: PropTypes.string
  })),
  locales: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })),
  localeSuggestions: PropTypes.arrayOf(PropTypes.shape({
    count: PropTypes.number.isRequired,
    locale: PropTypes.string.isRequired,
    path_pattern: PropTypes.string.isRequired
  })),
  localesFetchingStatus: PropTypes.string,
  moveToPage: PropTypes.func.isRequired,
  onBulkApply: PropTypes.func.isRequired,
  onBulkApplyAll: PropTypes.func.isRequired,
  onPathSearch: PropTypes.func.isRequired,
  paginationCurrentPage: PropTypes.number,
  paginationTotalPages: PropTypes.number,
  projectSlug: PropTypes.string.isRequired,
  totalCount: PropTypes.number
}

export default IssuesList
